import { CheckboxWithLabel } from 'components/CheckboxWithLabel';
import { Box } from '@mui/material';
import type {
  UnitInfoBananaScreenInterface,
  UnitInfoCMScreenInterface,
  UnitInfoCrusherInterface,
  UnitInfoCrusherVSIInterface,
  UnitInfoFeederScreenInterface,
  UnitInfoFeedInterface,
  UnitInfoFreeFallScreenSFInterface,
  UnitInfoFreeFallScreenSSInterface,
  UnitInfoGenericCrusherInterface,
  UnitInfoGenericScreenInterface,
  UnitInfoGrizzlyFeederInterface,
  UnitInfoGrizzlyScreenSGInterface,
  UnitInfoGyratoryCrusherCGInterface,
  UnitInfoHybridCrusherInterface,
  UnitInfoImpactCrusherCIInterface,
  UnitInfoInterface,
  UnitInfoJawCrusherInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoLMScreenInterface,
  UnitInfoPanFeederInterface,
  UnitInfoPlateFeederInterface,
  UnitInfoStorageInterface,
} from 'store/units/types';
import * as S from '../PropertyPanel.styles';
import { useEditUnitInfo } from '../util/hooks/useEditUnitInfo';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';
import { withSelectedUnit } from './withSelectedUnit';
import PropertyPanelAccordion from './PropertyPanelAccordion';
import { isProd } from 'util/env';
import useAuth, { UserRole } from 'auth/useAuth';
import { useIsRiskAssessmentEligible } from 'hooks/useRiskAssessmentUnits';

type Props<T extends UnitInfoInterface> = {
  id: string;
  unitInfo: T;
  expanded?: boolean;
  showDisplayLoad?: boolean;
  extra?: React.ReactNode;
};

type Labels<T> = Record<keyof T, string>;

/**
 * The order of this object will determin the
 * order of the check boxes in the UI
 */
const labels: Labels<keyof UnitInfoInterface['display']> = {
  displayUnitName: 'Unit name',
  displayModelName: 'Model name',
  displayDescription: 'Description',
  displayNbrOfDecks: 'Number of decks',
  displayNumberOfDecks: 'Number of decks',
  displayWetScreening: 'Type of screening',
  displayInclination: 'Inclination',
  displayConcave: 'Concave ',
  displayMantle: 'Mantle',
  displayThrow: 'Throw',
  displayBushing: 'Bushing',
  displayCss: 'CSS',
  displayInstalledMotorPower: 'Installed motor power',
  displayTipSpeed: 'Tip speed',
  displayMinLiveBin: 'Minimum live bin',
  displayUnitThrow: 'Throw',
  displayOss: 'OSS',
  displayRollDiameter: 'Roll diameter',
  displayRollWidth: 'Roll width',
  displaySegment: 'Segment',
  displayGapSetting: 'Adapted gap setting',
  displayCompStrength: 'Compressive strength',
  displaySetting: 'Setting',
  displayMotorPower: 'Motor power',
  displayNominalCapacityRange: 'Nominal capacity range',
  displayTotalStoredMass: 'Flow rate',
  displayProductSize: 'Product size',
  displayLoad: 'Load',
  displayRiskLevel: 'Risk level',
  displayMaterialName: 'Material name',
  displayFeedRate: 'Feed rate',
  displayBulkDensity: 'Bulk density',
  displaySpecificGravity: 'Specific gravity',
  displayMoisture: 'Moisture',
  displayClay: 'Clay',
  displayWorkIndex: 'Work index',
  displayCompensatedWorkIndex: 'Compensated work index',
  displayAbrasionIndex: 'Abrasion index',
  displayPSDFeedCurveName: 'PSD feed curve name',
  displayAttachmentTool: 'Attachment tool',
};

const labelOrder = Object.keys(labels);

const entriesByLabelOrder = (a: [string, unknown], b: [string, unknown]) =>
  labelOrder.indexOf(a[0]) - labelOrder.indexOf(b[0]);

const defined = (key: string) => Object.keys(labels).includes(key);

const getDisplayEntries = <T extends UnitInfoInterface>(
  unitInfo: T,
): Array<[keyof T['display'], T['display']]> => {
  if (!unitInfo.display) {
    return [];
  }

  // TODO: ATD Feature Toggle
  return Object.entries(unitInfo.display)
    .filter(([k]) => defined(k))
    .filter(([k]) => !(isProd() && k === 'displayAttachmentTool'))
    .sort(entriesByLabelOrder) as unknown as Array<
    [keyof T['display'], T['display']]
  >;
};

type UnitInfoWithDisplay =
  | UnitInfoBananaScreenInterface
  | UnitInfoCMScreenInterface
  | UnitInfoCrusherInterface
  | UnitInfoCrusherVSIInterface
  | UnitInfoFreeFallScreenSFInterface
  | UnitInfoFreeFallScreenSSInterface
  | UnitInfoGrizzlyFeederInterface
  | UnitInfoGrizzlyScreenSGInterface
  | UnitInfoGyratoryCrusherCGInterface
  | UnitInfoHybridCrusherInterface
  | UnitInfoImpactCrusherCIInterface
  | UnitInfoJawCrusherInterface
  | UnitInfoKwataniScreenInterface
  | UnitInfoLMScreenInterface
  | UnitInfoPanFeederInterface
  | UnitInfoPlateFeederInterface
  | UnitInfoFeedInterface
  | UnitInfoFeederScreenInterface
  | UnitInfoStorageInterface
  | UnitInfoGenericCrusherInterface
  | UnitInfoGenericScreenInterface;

const DisplayCard = <T extends UnitInfoWithDisplay>({
  id,
  unitInfo,
  expanded: _expanded = false,
  showDisplayLoad = true,
  extra,
}: Props<T>) => {
  const displayEntries = getDisplayEntries(unitInfo);
  const { showLoad, showEquipmentType, showRiskLevel } = useSelector(
    (state: RootState) => state.settingsState.unitDisplay,
  );
  const { setValue } = useEditUnitInfo(id, unitInfo);
  const { checkIfRoleIsIncluded } = useAuth();
  const isApprover = checkIfRoleIsIncluded(UserRole.PGROLE);
  const hasRiskAssessment = useIsRiskAssessmentEligible(id);

  const isDisabled = (key: string) => {
    if (key === 'displayLoad') {
      return !showLoad;
    }

    if (key === 'displayUnitName') {
      return !showEquipmentType;
    }
    if (key === 'displayAttachmentTool') {
      return !(
        unitInfo as
          UnitInfoJawCrusherInterface | UnitInfoGyratoryCrusherCGInterface
      ).attachmentTool.enabled;
    }
  };

  if (displayEntries.length === 0) {
    return null;
  }

  const visibleEntries = displayEntries.filter(([key, value]) => {
    if (typeof value !== 'boolean') return false;
    if (key === 'displayLoad' && !showDisplayLoad) return false;
    if (
      key === 'displayRiskLevel' &&
      (!isApprover || !showRiskLevel || !hasRiskAssessment)
    )
      return false;
    return true;
  });

  const enabledEntries = visibleEntries.filter(
    ([key]) => !isDisabled(String(key)),
  );

  const selectAll = () => {
    enabledEntries.forEach(([key]) =>
      setValue(['display', key], true as T['display'][keyof T['display']]),
    );
  };

  const unselectAll = () => {
    enabledEntries.forEach(([key]) =>
      setValue(['display', key], false as T['display'][keyof T['display']]),
    );
  };

  const allChecked =
    enabledEntries.length > 0 &&
    enabledEntries.every(([, value]) => (value as unknown as boolean) === true);

  return (
    <PropertyPanelAccordion title='Display'>
      <Box sx={{ display: 'flex' }}>
        <CheckboxWithLabel
          value={allChecked}
          setValue={(val) => (val ? selectAll() : unselectAll())}
          label='All'
        />
      </Box>
      <S.Line />
      <S.CheckBoxContainer>
        {displayEntries.map(([key, value]) => {
          if (typeof value === 'boolean') {
            if (key === 'displayLoad' && !showDisplayLoad) return null;
            if (
              key === 'displayRiskLevel' &&
              (!isApprover || !showRiskLevel || !hasRiskAssessment)
            )
              return null;
            return (
              <CheckboxWithLabel
                value={value}
                key={String(key)}
                setValue={(val) =>
                  setValue(
                    ['display', key],
                    val as T['display'][keyof T['display']],
                  )
                }
                label={labels[key]}
                disabled={isDisabled(String(key))}
              />
            );
          }
          return null;
        })}
        {!!extra && extra}
      </S.CheckBoxContainer>
    </PropertyPanelAccordion>
  );
};

export default withSelectedUnit(DisplayCard);
